import {
  Box,
  Button,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useAuthRedirect } from './AuthButtons'
import { usePageTitle } from '@/utils/hooks/usePageTitle'

interface InviteAuthPromptProps {
  action: 'login' | 'signup'
}

// Invite landing page biased towards a single primary action. The emailed
// invitation link carries ?action=login|signup depending on whether the
// invitee already has an account, so they see the right prompt first instead
// of an equal-weight login/signup page. The alternative stays available as a
// small secondary link.
export default function InviteAuthPrompt({ action }: InviteAuthPromptProps) {
  usePageTitle(action === 'login' ? 'Log in' : 'Sign up')

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { login, signup } = useAuthRedirect()

  const isLogin = action === 'login'

  const bodyText = isLogin
    ? 'It looks like you already have a Fundsorter login. To view your invitation and join the team, please log in using the same email address this invitation was sent to.'
    : "Welcome! To accept your invitation and join the team, you'll first need to create your personal login. Please sign up using the same email address this invitation was sent to. You may be asked to verify your email address before you can continue."

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        width: isMobile ? '100%' : '50%',
        margin: '0 auto',
        height: '80vh',
        textAlign: 'center',
        px: isMobile ? 2 : 0,
      }}
    >
      <img
        src="/Fundsorter-h-black800px.png"
        alt="Fundsorter Logo"
        width={isMobile ? '70%' : '50%'}
      />

      <Typography variant="h5" component="h1" sx={{ mt: 4, color: '#1C3045' }}>
        You've been invited to join an organisation on Fundsorter
      </Typography>

      <Typography sx={{ mt: 2, maxWidth: 480, color: '#1C3045' }}>
        {bodyText}
      </Typography>

      <Button
        disableRipple
        variant="contained"
        onClick={isLogin ? login : signup}
        sx={{
          borderRadius: 3,
          bgcolor: '#1C3045',
          color: 'white',
          mt: 4,
          width: isMobile ? '80%' : '50%',
          maxWidth: 450,
          fontSize: '22px',
          textTransform: 'none',
        }}
      >
        {isLogin ? 'Log in' : 'Sign up'}
      </Button>

      <Typography sx={{ mt: 3, color: '#1C3045' }}>
        {isLogin
          ? "Don't have an account yet?"
          : 'Already have a Fundsorter account?'}{' '}
        <Link
          component="button"
          type="button"
          onClick={isLogin ? signup : login}
          sx={{ color: '#1C3045', fontWeight: 600 }}
        >
          {isLogin ? 'Sign up instead' : 'Log in instead'}
        </Link>
      </Typography>
    </Box>
  )
}
